import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Home, Search, Ticket, User, Plus, Plane, MapPin, Check, Clock } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import Logo from "../../components/Logo";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

// Coinciden exacto con las claves que reconoce StatusBadge (styles.*).
const STATUS_LABELS = {
  pendiente_pago: "Pendiente",
  confirmado: "Confirmado",
  completado: "Completado",
  cancelado: "Cancelado",
};

const BoardingPass = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const reservationId = searchParams.get("reservationId");
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reservationId) {
      setLoading(false);
      return;
    }
    clientApi
      .getReservation(reservationId)
      .then(({ data }) => setReservation(data))
      .finally(() => setLoading(false));
  }, [reservationId]);

  if (loading) {
    return (
      <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="boarding-pass-page">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12 text-sm text-[#A0A0A5] font-body">Cargando…</div>
      </PanelShell>
    );
  }

  if (!reservation) {
    return (
      <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="boarding-pass-page">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
          <p className="text-sm text-[#A0A0A5] font-body">
            No se encontró la reserva. <Link to="/cliente/historial" className="text-[#C9A227]">Ver mis reservas</Link>
          </p>
        </div>
      </PanelShell>
    );
  }

  const scheduled = new Date(reservation.scheduled_at);

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="boarding-pass-page">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex w-16 h-16 rounded-full bg-[#C9A227]/15 border border-[#C9A227] items-center justify-center mb-5">
            <Check size={26} className="text-[#C9A227]" />
          </div>
          <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Reserva creada</p>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-2">Tu boarding pass ejecutivo</h1>
          <p className="mt-3 text-sm text-[#A0A0A5] font-body">Guarda este ticket. Tu conductor te contactará antes del viaje.</p>
        </div>

        <div className="relative" data-testid="boarding-pass-ticket">
          <div className="relative bg-[#1C1D20] border border-[#C9A227]/30 rounded-2xl overflow-hidden">
            <div className="p-6 lg:p-8 border-b border-dashed border-[#2E2F33] flex items-center justify-between">
              <Logo size="md" to={null} />
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5]">Reserva</p>
                <p className="font-mono text-sm text-[#C9A227]">{reservation.id.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>

            <div className="p-6 lg:p-10 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 items-center">
              <div>
                <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">Modalidad</p>
                <p className="font-display font-bold text-2xl lg:text-3xl mt-2">{reservation.modality === "compartido" ? "Compartido" : "Privado"}</p>
                {reservation.seat_numbers.length > 0 && (
                  <p className="text-xs text-[#A0A0A5] font-body mt-1">Asientos: {reservation.seat_numbers.join(", ")}</p>
                )}
                <p className="text-sm text-[#F5F5F5] mt-3 flex items-center gap-2">
                  <Clock size={12} className="text-[#C9A227]" /> {scheduled.toLocaleString("es-PE")}
                </p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <Plane size={22} className="text-[#C9A227] rotate-90" />
                <div className="w-24 border-t border-dashed border-[#C9A227]/40" />
              </div>

              <div className="text-right">
                <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">Destino</p>
                <p className="text-sm text-[#F5F5F5] mt-3 flex items-center justify-end gap-2">
                  <MapPin size={12} className="text-[#C9A227]" /> {reservation.destination_address || "Según ruta compartida"}
                </p>
              </div>
            </div>

            <div className="relative border-t border-dashed border-[#2E2F33]">
              <div className="absolute -left-3 -top-3 w-6 h-6 rounded-full bg-[#0D0E10]" />
              <div className="absolute -right-3 -top-3 w-6 h-6 rounded-full bg-[#0D0E10]" />
            </div>

            <div className="p-6 lg:p-8 grid grid-cols-2 md:grid-cols-4 gap-5">
              <Info label="Pasajero" value={reservation.contact_name} />
              <Info label="Vuelo" value={reservation.flight_number || "—"} />
              <Info label="Pago" value={`${reservation.payment_method} · S/ ${reservation.price_total}`} />
              <Info label="Estado" value={<StatusBadge status={STATUS_LABELS[reservation.status]} />} />
              <Info label="Estado de pago" value={reservation.payment_status === "pagado" ? "Pagado" : "Pendiente"} />
              <Info label="Fecha de creación" value={new Date(reservation.creado_en).toLocaleString("es-PE")} />
            </div>
          </div>
        </div>

        <Link to="/cliente" className="mt-6 block text-center text-xs uppercase tracking-brand text-[#A0A0A5] hover:text-[#C9A227] font-display" data-testid="back-dashboard">
          Volver al panel
        </Link>
      </div>
    </PanelShell>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5] font-display mb-1">{label}</p>
    <div className="text-sm font-body text-[#F5F5F5]">{value}</div>
  </div>
);

export default BoardingPass;
